// @flow
import { filter, map, sortBy } from 'lodash';

// One character, as the search compares it: without accents, in lowercase ("Ã" → "a"; a lone combining accent → "")
const plain = (char: string): string => char.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/**
 * The parts of a name to show in bold for the typed words (plainWords): every place each appears, ignoring accents
 * and capitals ("joao" marks "João"). Returns the name cut into { text, match, at } pieces, in order (at: where the piece
 * starts, a stable key). Works on code points, so emoji and accents typed as separate marks keep the bold in place.
 */
// What was typed, as the comparison sees it: its words, plain
export const plainWords = (typed: string): Array<string> => filter(map(String(typed || '').trim().split(/\s+/), (word) => map(Array.from(word), plain).join('')));

// A word of one or two letters marks only where a word starts ("ana s" doesn't bold every "s" in the name)
const SHORT_WORD = 2;

export const highlightMatch = (name: string, words: Array<string>): Array<{ text: string, match: boolean, at: number }> => {
  const chars = Array.from(name || '');
  // The plain text, and for each of its characters the code point of the name it came from
  let plainName = '';
  const owner = [];
  chars.forEach((char, index) => {
    const plainChar = plain(char);
    plainName += plainChar;
    for (let i = 0; i < plainChar.length; i += 1) owner.push(index);
  });

  const ranges = [];
  words.forEach((word) => {
    let found = plainName.indexOf(word);
    while (found !== -1) {
      const startsWord = found === 0 || /\s/.test(plainName[found - 1]);
      if (word.length > SHORT_WORD || startsWord) {
        let end = owner[found + word.length - 1] + 1;
        // An accent stored as its own mark after the last letter goes with it
        while (end < chars.length && plain(chars[end]) === '') end += 1;
        ranges.push([owner[found], end]);
      }
      found = plainName.indexOf(word, found + word.length);
    }
  });
  if (!ranges.length) return [{ text: name || '', match: false, at: 0 }];

  const pieces = [];
  let at = 0;
  sortBy(ranges, (range) => range[0]).forEach(([start, end]) => {
    const from = Math.max(start, at);
    if (from >= end) return;
    if (from > at) pieces.push({ text: chars.slice(at, from).join(''), match: false, at });
    pieces.push({ text: chars.slice(from, end).join(''), match: true, at: from });
    at = end;
  });
  if (at < chars.length) pieces.push({ text: chars.slice(at).join(''), match: false, at });
  return pieces;
};
